import { zodResolver } from "@hookform/resolvers/zod";
import { pick } from "es-toolkit";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useBreadCrumbContext } from "@/contexts/breadcrumb";
import {
	useLabelPort,
	useLocalStorage,
	usePrint,
	usePrinters,
	useSerialPorts,
} from "@/hooks";
import {
	Button,
	FormContainer,
	FormSection,
	ReactSelect,
	VatRateSelect,
} from "../../components";
import {
	columnsForPageSize,
	columnsOptions,
	pageSizeOptions,
	printerColumns,
	printTestData,
	settingsSchema,
	type settingsSchemaType,
} from "./config";

const Settings = () => {
	const { t } = useTranslation();
	useBreadCrumbContext([{ label: t("settings.pageTitle") }]);
	const [selectedPrinter, setSelectedPrinter, removePrinter] = useLocalStorage(
		"printer",
		undefined,
	);
	const [defaultVatRateId, setDefaultVatRateId] = useLocalStorage(
		"defaultVatRateId",
		undefined,
	);

	const { control, handleSubmit, setValue, watch } =
		useForm<settingsSchemaType>({
			resolver: zodResolver(settingsSchema),
			defaultValues: { name: "", pageSize: undefined, columns: undefined },
		});

	const { data: printers, isSuccess } = usePrinters();
	const { mutate: print } = usePrint();

	// The label printer's serial port. Hardcoded to COM2 in the Electron build,
	// so a till wired any other way could not print labels at all.
	const [labelPort, setLabelPort] = useLabelPort();
	const { data: serialPorts } = useSerialPorts();

	const [initialRender, setInitialRender] = useState(true);

	useEffect(() => {
		if (selectedPrinter && isSuccess && initialRender) {
			const findPrinter = printers?.find(
				(p) => p.name === selectedPrinter.name,
			);
			if (findPrinter) {
				setValue("name", selectedPrinter.name);
				setValue("pageSize", selectedPrinter.pageSize);
				setValue("columns", selectedPrinter.columns);
			} else {
				removePrinter();
			}
			setInitialRender(false);
		}
	}, [
		isSuccess,
		selectedPrinter,
		printers,
		removePrinter,
		setValue,
		initialRender,
	]);

	const options = useMemo(() => {
		return printers
			? printers.map((printer) => ({
					value: printer.name,
					label: printer.name,
				}))
			: [];
	}, [printers]);

	const serialPortOptions = useMemo(() => {
		return (serialPorts ?? []).map((port) => ({
			value: port.path,
			// A bare "COM3" tells an operator nothing; the USB descriptor does.
			label:
				port.product || port.manufacturer
					? `${port.path} -- ${port.product || port.manufacturer}`
					: port.path,
		}));
	}, [serialPorts]);

	const onSavePrinter = useCallback(
		(data: settingsSchemaType) => {
			setSelectedPrinter(pick(data, ["name", "pageSize", "columns"]));
			setDefaultVatRateId(data.defaultVatRateId || undefined);
			toast.success(t("global.saved"));
		},
		[setSelectedPrinter, setDefaultVatRateId, t],
	);

	// Prints what is saved, so the ruler matches what receipts will use.
	const onPrintTest = () => {
		print(printTestData(printerColumns(selectedPrinter)));
	};

	// Shown in the characters-per-line placeholder: what an unset value means.
	const guessedColumns = columnsForPageSize(watch("pageSize"));

	useEffect(() => {
		setValue("defaultVatRateId", defaultVatRateId);
	}, [setValue, defaultVatRateId]);

	return (
		<FormContainer
			isNotForm
			title={t("settings.pageTitle")}
			subtitle={t("settings.pageSubtitle")}
		>
			<form
				className="flex flex-col gap-8"
				onSubmit={handleSubmit(onSavePrinter)}
			>
				<FormSection
					title={t("settings.receiptPrinter.title")}
					description={t("settings.receiptPrinter.description")}
				>
					<Controller
						control={control}
						name="name"
						render={({ field: { onChange, value }, fieldState: { error } }) => (
							<ReactSelect
								error={error?.message}
								onChange={(item) => onChange(item?.value)}
								value={{ value, label: value }}
								placeholder={t("settings.receiptPrinter.printerPlaceholder")}
								options={options}
								label={t("settings.receiptPrinter.printerLabel")}
								hint={t("settings.receiptPrinter.printerHint")}
							/>
						)}
					/>
					<Controller
						control={control}
						name="pageSize"
						render={({ field: { onChange, value }, fieldState: { error } }) => (
							<ReactSelect
								error={error?.message}
								onChange={(item) => onChange(item?.value)}
								value={{ value, label: value }}
								placeholder={t("settings.receiptPrinter.paperPlaceholder")}
								options={pageSizeOptions}
								label={t("settings.receiptPrinter.paperLabel")}
								hint={t("settings.receiptPrinter.paperHint")}
							/>
						)}
					/>
					<Controller
						control={control}
						name="columns"
						render={({ field: { onChange, value }, fieldState: { error } }) => (
							<ReactSelect
								error={error?.message}
								onChange={(item) => onChange(item?.value)}
								value={value ? { value, label: String(value) } : null}
								isClearable
								placeholder={t("settings.receiptPrinter.columnsPlaceholder", {
									columns: guessedColumns,
								})}
								options={columnsOptions}
								label={t("settings.receiptPrinter.columnsLabel")}
								hint={t("settings.receiptPrinter.columnsHint")}
							/>
						)}
					/>

					<div className="flex gap-4 self-end">
						{selectedPrinter && (
							<Button variant="outline" type="button" onClick={onPrintTest}>
								{t("settings.receiptPrinter.test")}
							</Button>
						)}
						<Button type="submit">{t("global.save")}</Button>
					</div>
				</FormSection>
				<FormSection
					title={t("settings.labelPrinter.title")}
					description={t("settings.labelPrinter.description")}
				>
					<ReactSelect
						onChange={(item) => setLabelPort(item?.value)}
						value={{ value: labelPort, label: labelPort }}
						placeholder={t("settings.labelPrinter.portPlaceholder")}
						options={serialPortOptions}
						label={t("settings.labelPrinter.portLabel")}
						hint={t("settings.labelPrinter.portHint")}
					/>
				</FormSection>
				<FormSection
					title={t("settings.defaultVatRate.title")}
					description={t("settings.defaultVatRate.description")}
				>
					<Controller
						control={control}
						name="defaultVatRateId"
						render={({ field: { onChange, value }, fieldState: { error } }) => (
							<VatRateSelect
								error={error?.message}
								onChange={onChange}
								value={value}
								label={t("product.vatLabel")}
								placeholder={t("product.vatPlaceholder")}
							/>
						)}
					/>

					<div className="flex gap-4 self-end">
						<Button type="submit">{t("global.save")}</Button>
					</div>
				</FormSection>
			</form>
		</FormContainer>
	);
};

export default Settings;
